import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    endpointGetReportMetadataList,
    endpointGetChannelsWithReports,
    endpointSyncReportsOptimized,
    endpointSyncReportsAll,
    urlTasks,
} from '../services/Endpoints';
import { Loading } from './Loading';
import { Dropdown } from 'react-bootstrap';
import { theme } from '../theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faUpload, faEye } from '@fortawesome/free-solid-svg-icons';
import UploadReport from './UploadReport';
import SyncConfirmationModal from './SyncConfirmationModal';
import NotActivatedModal from './NotActivatedModal';

// ──────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────
const POLL_INTERVAL_MS = 30000; // cada 30 seg mientras haya registros en progreso

const formatDate = (isoString) => {
    if (!isoString) return '—';
    const d = new Date(isoString);
    return d.toLocaleString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const SYNC_STATUS_MAP = {
    completed: { label: 'Completado', cls: 'sync-badge sync-badge--completed' },
    in_progress: { label: 'Sincronizando', cls: 'sync-badge sync-badge--progress' },
};

const SyncStatusBadge = ({ status }) => {
    const entry = SYNC_STATUS_MAP[status] || { label: status || 'Sin sync', cls: 'sync-badge sync-badge--pending' };
    const isProgress = status === 'in_progress';
    return (
        <span className={entry.cls}>
            {isProgress && <FontAwesomeIcon icon={faSync} spin className="me-1" style={{ fontSize: '0.7rem' }} />}
            {entry.label}
        </span>
    );
};

// Un reporte está "listo para sincronizar" si no está completado ni en progreso
const canSync = (rec) => rec && rec.sync_status !== 'completed' && rec.sync_status !== 'in_progress';

// ──────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────
function StoreReportMetadataList() {
    const [loading, setLoading] = useState(false);
    const [records, setRecords] = useState([]);
    const [channels, setChannels] = useState([]);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [showUpload, setShowUpload] = useState(false);
    const [showSyncConfirmation, setShowSyncConfirmation] = useState(false);
    const [syncType, setSyncType] = useState('');
    const [showNotActivated, setShowNotActivated] = useState(false);

    const pollTimerRef = useRef(null);

    // ── Fetch channels
    const getChannels = useCallback(async () => {
        try {
            const response = await fetch(`${urlTasks}${endpointGetChannelsWithReports}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            const res = await response.json();
            if (res && res.status === 'success') {
                setChannels(res.data.channels || []);
            }
        } catch (error) {
            console.error('Error fetching channels:', error);
        }
    }, []);

    // ── Fetch historial (silencioso para polling, con loader para manual)
    const getMetadataList = useCallback(async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            const response = await fetch(`${urlTasks}${endpointGetReportMetadataList}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            const res = await response.json();
            if (res && res.status === 'success') {
                setRecords(res.data.records || []);
            } else {
                setRecords([]);
            }
        } catch (error) {
            console.error('Error fetching report metadata list:', error);
            setRecords([]);
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    // ── Auto-polling: activo mientras haya algún registro in_progress
    useEffect(() => {
        const hasInProgress = records.some(r => r.sync_status === 'in_progress');

        if (hasInProgress) {
            // Si no hay timer activo, arrancarlo
            if (!pollTimerRef.current) {
                pollTimerRef.current = setInterval(() => {
                    getMetadataList(true); // silencioso: sin spinner global
                }, POLL_INTERVAL_MS);
            }
        } else {
            // No hay nada en progreso, detener polling
            if (pollTimerRef.current) {
                clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
            }
        }

        return () => {
            if (pollTimerRef.current) {
                clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
            }
        };
    }, [records, getMetadataList]);

    useEffect(() => {
        getChannels();
        getMetadataList();
    }, [getChannels, getMetadataList]);

    // Si la fila seleccionada cambió su estado a in_progress o completed, actualizar referencia
    useEffect(() => {
        if (selectedRecord) {
            const updated = records.find(
                r => r.channel === selectedRecord.channel && r.upload_date === selectedRecord.upload_date
            );
            if (updated && updated !== selectedRecord) {
                setSelectedRecord(updated);
            }
        }
    }, [records, selectedRecord]);

    // ── Canal del reporte seleccionado
    const selectedChannel = selectedRecord?.channel || '';
    const channelLabel = channels.find(ch => ch.alias === selectedChannel)?.name || selectedChannel;

    // ── Sincronización
    const handleSyncReports = async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `${urlTasks}${endpointSyncReportsOptimized}?channel=${selectedChannel}`,
                { method: 'POST', headers: { 'Content-Type': 'application/json' } }
            );
            const res = await response.json();
            if (res && res.status === 'success') {
                alert(`Se ha iniciado la sincronización de ${channelLabel}. Se demorará unos minutos.`);
                // Refrescar para capturar el estado in_progress (ya guardado en BD)
                getMetadataList();
            } else {
                alert('Error al sincronizar: ' + (res.message || 'Error desconocido'));
            }
        } catch (error) {
            alert('Error al sincronizar: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSyncAllReports = async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `${urlTasks}${endpointSyncReportsAll}?channel=${selectedChannel}&type=${syncType}`,
                { method: 'GET', headers: { 'Content-Type': 'application/json' } }
            );
            const res = await response.json();
            if (res && res.status === 'success') {
                alert(`Sincronización completa de ${channelLabel} iniciada. Se demorará unos minutos.`);
                // Refrescar para capturar el estado in_progress
                getMetadataList();
            } else {
                alert('Error al sincronizar todo: ' + (res.message || 'Error desconocido'));
            }
        } catch (error) {
            alert('Error al sincronizar todo: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleShowSyncConfirmation = (type) => {
        setSyncType(type);
        setShowSyncConfirmation(true);
    };

    const handleConfirmSync = async () => {
        setShowSyncConfirmation(false);
        if (syncType === 'partial') await handleSyncReports();
        else if (syncType === 'full' || syncType === 'not_found') await handleSyncAllReports();
    };

    const handleUploadSuccess = () => {
        setSelectedRecord(null);
        getMetadataList();
    };

    const handleRowClick = (rec) => {
        setSelectedRecord(selectedRecord === rec ? null : rec);
    };

    // Dropdown activo solo si hay fila seleccionada que se pueda sincronizar
    const syncEnabled = selectedRecord !== null && canSync(selectedRecord) && !loading;

    // ¿Hay algún registro sincronizando? (para mostrar indicador sutil)
    const hasSyncing = records.some(r => r.sync_status === 'in_progress');

    return (
        <>
            {/* ── Barra de acciones */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="text-white-50 small d-flex align-items-center gap-2">
                    {hasSyncing && (
                        <span style={{ color: theme.primary }}>
                            <FontAwesomeIcon icon={faSync} spin className="me-1" />
                            Sincronización en curso…
                        </span>
                    )}
                    {!hasSyncing && (selectedRecord
                        ? <>Reporte seleccionado: <strong className="text-white">{channelLabel}</strong> — {formatDate(selectedRecord.upload_date)}</>
                        : 'Selecciona un reporte de la tabla para sincronizar'
                    )}
                </div>

                <div className="d-flex gap-2 align-items-center">
                    {/* Botón Actualizar */}
                    <button
                        className="btn btn-ink-icon"
                        onClick={() => getMetadataList(false)}
                        disabled={loading}
                        title="Recargar lista"
                    >
                        <FontAwesomeIcon icon={faSync} spin={loading} />
                    </button>

                    {/* Botón Sin Sincronizar */}
                    <button
                        className="btn btn-outline-warning d-flex align-items-center gap-2 shadow-sm"
                        onClick={() => setShowNotActivated(true)}
                        disabled={!selectedRecord || loading}
                        title={selectedRecord ? `Ver registros sin sincronizar de ${channelLabel}` : 'Selecciona un reporte primero'}
                    >
                        <FontAwesomeIcon icon={faEye} />
                        Sin Sincronizar
                    </button>

                    {/* Dropdown Sincronizar */}
                    <Dropdown>
                        <Dropdown.Toggle
                            variant="success"
                            id="dropdown-sync-metadata"

                        >
                            Sincronizar{syncEnabled ? ` ${channelLabel}` : ''}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {/* <Dropdown.Item onClick={() => handleShowSyncConfirmation('partial')}>
                                Sincronización Parcial (Solo notificaciones)
                            </Dropdown.Item> */}
                            <Dropdown.Item onClick={() => handleShowSyncConfirmation('full')} disabled={!syncEnabled}>
                                Todo el Catálogo
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => handleShowSyncConfirmation('not_found')}>
                                No Encontrados
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>

                    {/* Botón Cargar Reporte */}
                    <button
                        className="btn btn-primary d-flex align-items-center gap-2 shadow-sm"
                        onClick={() => setShowUpload(true)}
                        disabled={loading}
                    >
                        <FontAwesomeIcon icon={faUpload} />
                        Cargar Reporte
                    </button>
                </div>
            </div>

            {loading && <Loading />}

            {/* ── Tabla historial */}
            <div className="modern-table-container">
                <table className="modern-table table-hover">
                    <thead>
                        <tr>
                            <th>Tienda</th>
                            <th>Fecha de Carga</th>
                            <th className="text-center">Total Registros</th>
                            <th className="text-center">Activos</th>
                            <th className="text-center">Inactivos</th>
                            <th>Última Sincronización</th>
                            <th className="text-center">Estado Sync</th>
                            <th className="text-center">Sincronizados</th>
                        </tr>
                    </thead>
                    <tbody>
                        {!loading && records.length === 0 && (
                            <tr>
                                <td colSpan={8} className="text-center p-4">
                                    No hay registros de cargas disponibles
                                </td>
                            </tr>
                        )}
                        {records.map((rec, idx) => {
                            const isSelected = selectedRecord === rec;
                            const isSyncing = rec.sync_status === 'in_progress';
                            const rowStyle = {
                                cursor: 'pointer',
                                background: isSelected
                                    ? `rgba(${theme.primaryRgb}, 0.15)`
                                    : isSyncing
                                        ? `rgba(${theme.primaryRgb}, 0.06)`
                                        : undefined,
                                transition: 'background 0.2s',
                            };

                            return (
                                <tr
                                    key={idx}
                                    onClick={() => handleRowClick(rec)}
                                    style={rowStyle}
                                    title={
                                        isSyncing ? 'Sincronización en curso…'
                                            : canSync(rec) ? 'Clic para seleccionar y habilitar sincronización'
                                                : 'Este reporte ya fue sincronizado'
                                    }
                                >
                                    <td>
                                        <span className="fw-semibold">
                                            {channels.find((ch) => ch.alias === rec.channel)?.name || rec.channel}
                                        </span>
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{formatDate(rec.upload_date)}</td>
                                    <td className="text-center">{rec.total_records?.toLocaleString() ?? '—'}</td>
                                    <td className="text-center">
                                        <span className="badge-status status-on">{rec.active_count?.toLocaleString() ?? '—'}</span>
                                    </td>
                                    <td className="text-center">
                                        <span className="badge-status status-off">
                                            {rec.inactive_count?.toLocaleString() ?? '—'}
                                        </span>
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{formatDate(rec.last_sync_date)}</td>
                                    <td className="text-center">
                                        <SyncStatusBadge status={rec.sync_status} />
                                    </td>
                                    <td className="text-center">{rec.synced_count?.toLocaleString() ?? '—'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* ── Modales */}
            <UploadReport
                showUpload={showUpload}
                setShowUpload={setShowUpload}
                channels={channels}
                onUploadSuccess={handleUploadSuccess}
            />

            <SyncConfirmationModal
                isOpen={showSyncConfirmation}
                onClose={() => setShowSyncConfirmation(false)}
                onConfirm={handleConfirmSync}
                syncType={syncType}
                selectedChannel={channelLabel}
            />

            <NotActivatedModal
                isOpen={showNotActivated}
                onClose={() => setShowNotActivated(false)}
                channel={selectedChannel}
                channelLabel={channelLabel}
                reportDate={selectedRecord?.upload_date}
            />
        </>
    );
}

export default StoreReportMetadataList;
